// Smart Assignment Algorithm for Shift Setup
// Scores each server-section pairing based on multiple factors

export interface Guest {
  id: string;
  firstName: string;
  lastName: string;
  isVIP?: boolean;
  allergies?: string[];
  dietaryRestrictions?: string[];
  preferences?: {
    preferredServer?: string;
    preferredSection?: string;
    [key: string]: unknown;
  };
}

export interface Reservation {
  id: string;
  guestName: string;
  guestId?: string;
  partySize: number;
  date: string;
  time: string;
  tableId?: string;
  tableIds?: string[];
  notes?: string;
  status?: string;
  guest?: Guest;
}

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
  skills?: string[];
  allergyTraining?: string[];
  sectionHistory?: Record<string, number>; // sectionId -> times worked
}

export interface Section {
  id: string;
  name: string;
  color?: string;
  tableIds?: string[];
}

export interface AssignmentScore {
  employeeId: string;
  sectionId: string;
  totalScore: number;
  breakdown: {
    vipPreference: number;
    allergyAwareness: number;
    sectionExperience: number;
    workloadBalance: number;
    skillMatch: number;
  };
  reasons: string[];
}

export interface ScoringContext {
  employees: Employee[];
  sections: Section[];
  reservations: Reservation[];
  guests: Guest[];
  existingAssignments: Array<{ employeeId: string; sectionId?: string }>;
}

// Score weights
const WEIGHTS = {
  VIP_PREFERENCE: 30,
  ALLERGY_AWARENESS: 20,
  SECTION_EXPERIENCE: 20,
  WORKLOAD_BALANCE: 15,
  SKILL_MATCH: 15,
};

function reservationBelongsToSection(reservation: Reservation, section: Section): boolean {
  const sectionTableIds = section.tableIds || [];
  if (sectionTableIds.length === 0) return false;

  if (reservation.tableId && sectionTableIds.includes(reservation.tableId)) {
    return true;
  }

  if (reservation.tableIds?.some((tableId) => sectionTableIds.includes(tableId))) {
    return true;
  }

  return false;
}

// Calculate VIP preference score
function calculateVipPreferenceScore(
  employee: Employee,
  section: Section,
  reservations: Reservation[],
  guests: Guest[]
): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  let score = 0;

  // Find VIP reservations in this section's tables
  const sectionReservations = reservations.filter(
    (r) => reservationBelongsToSection(r, section)
  );

  const vipReservations = sectionReservations.filter((r) => {
    const guest = guests.find((g) => g.id === r.guestId);
    return guest?.isVIP;
  });

  for (const vipRes of vipReservations) {
    const guest = guests.find((g) => g.id === vipRes.guestId);
    if (guest?.preferences?.preferredServer === employee.id) {
      score = WEIGHTS.VIP_PREFERENCE;
      reasons.push(`VIP ${guest.firstName} ${guest.lastName} prefers ${employee.firstName}`);
      break;
    }
    if (guest?.preferences?.preferredSection === section.id) {
      score = Math.max(score, Math.round(WEIGHTS.VIP_PREFERENCE * 0.6));
      reasons.push(`VIP ${guest.firstName} ${guest.lastName} prefers this section`);
    }
    // Partial score if the VIP has worked with this server before
    if (guest?.preferences?.preferredServer) {
      // They prefer someone else, slight negative
      score = Math.max(score, 0);
    } else {
      // No preference, neutral
      score = Math.max(score, WEIGHTS.VIP_PREFERENCE * 0.3);
    }
  }

  // Bonus if server has VIP experience
  if (vipReservations.length > 0 && score === 0) {
    score = WEIGHTS.VIP_PREFERENCE * 0.2;
    reasons.push(`${vipReservations.length} VIP(s) in section`);
  }

  return { score, reasons };
}

// Calculate allergy awareness score
function calculateAllergyScore(
  employee: Employee,
  section: Section,
  reservations: Reservation[],
  guests: Guest[]
): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  let score = 0;

  // Find reservations with allergy concerns in this section
  const sectionReservations = reservations.filter(
    (r) => reservationBelongsToSection(r, section)
  );

  const allergiesInSection = new Set<string>();
  for (const res of sectionReservations) {
    const guest = guests.find((g) => g.id === res.guestId);
    guest?.allergies?.forEach((a) => allergiesInSection.add(a.toLowerCase()));
  }

  if (allergiesInSection.size === 0) {
    return { score: WEIGHTS.ALLERGY_AWARENESS, reasons: [] };
  }

  // Check if employee has training for these allergies
  const employeeAllergyTraining = employee.allergyTraining?.map((a) => a.toLowerCase()) || [];
  const coveredAllergies = Array.from(allergiesInSection).filter((a) =>
    employeeAllergyTraining.includes(a)
  );

  const coverageRatio = coveredAllergies.length / allergiesInSection.size;
  score = Math.round(WEIGHTS.ALLERGY_AWARENESS * coverageRatio);

  if (coveredAllergies.length > 0) {
    reasons.push(`Trained for: ${coveredAllergies.join(', ')}`);
  }
  if (coverageRatio < 1) {
    const uncovered = Array.from(allergiesInSection).filter(
      (a) => !employeeAllergyTraining.includes(a)
    );
    reasons.push(`Allergies need attention: ${uncovered.join(', ')}`);
  }

  return { score, reasons };
}

// Calculate section experience score
function calculateSectionExperienceScore(
  employee: Employee,
  section: Section
): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  const timesWorked = employee.sectionHistory?.[section.id] || 0;

  // Logarithmic scaling: diminishing returns after ~10 times
  const experienceFactor = Math.min(1, Math.log10(timesWorked + 1) / Math.log10(11));
  const score = Math.round(WEIGHTS.SECTION_EXPERIENCE * experienceFactor);

  if (timesWorked > 0) {
    reasons.push(`Worked ${section.name} ${timesWorked} times`);
  }

  return { score, reasons };
}

// Calculate workload balance score
function calculateWorkloadScore(
  section: Section,
  reservations: Reservation[],
  existingAssignments: Array<{ employeeId: string; sectionId?: string }>,
  allSections: Section[]
): { score: number; reasons: string[] } {
  const reasons: string[] = [];

  // Count reservations per section
  const resoCountBySection: Record<string, number> = {};
  for (const s of allSections) {
      resoCountBySection[s.id] = reservations.filter(
      (r) => reservationBelongsToSection(r, s)
    ).length;
  }

  // Calculate average
  const totalResos = Object.values(resoCountBySection).reduce((a, b) => a + b, 0);
  const avgPerSection = totalResos / Math.max(allSections.length, 1);

  const thisSectionResos = resoCountBySection[section.id] || 0;

  // Score higher for sections near or below average
  let balanceRatio: number;
  if (avgPerSection === 0) {
    balanceRatio = 1;
  } else {
    const deviation = Math.abs(thisSectionResos - avgPerSection) / avgPerSection;
    balanceRatio = Math.max(0, 1 - deviation * 0.5);
  }

  const score = Math.round(WEIGHTS.WORKLOAD_BALANCE * balanceRatio);

  if (thisSectionResos > avgPerSection * 1.3) {
    reasons.push(`High volume section (${thisSectionResos} reservations)`);
  } else if (thisSectionResos < avgPerSection * 0.7) {
    reasons.push(`Lower volume section`);
  } else {
    reasons.push(`Balanced workload`);
  }

  return { score, reasons };
}

// Calculate skill match score
function calculateSkillMatchScore(
  employee: Employee,
  section: Section,
  reservations: Reservation[]
): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  let score = WEIGHTS.SKILL_MATCH * 0.5; // Base score

  // Check for large parties
  const sectionReservations = reservations.filter(
    (r) => reservationBelongsToSection(r, section)
  );

  const hasLargeParty = sectionReservations.some((r) => r.partySize >= 6);
  const hasSpecialOccasion = sectionReservations.some(
    (r) => r.notes?.toLowerCase().includes('birthday') ||
           r.notes?.toLowerCase().includes('anniversary') ||
           r.notes?.toLowerCase().includes('celebration')
  );

  if (hasLargeParty && employee.skills?.includes('large_party')) {
    score += WEIGHTS.SKILL_MATCH * 0.3;
    reasons.push('Experience with large parties');
  }

  if (hasSpecialOccasion && employee.skills?.includes('special_occasions')) {
    score += WEIGHTS.SKILL_MATCH * 0.2;
    reasons.push('Good with special occasions');
  }

  return { score: Math.min(score, WEIGHTS.SKILL_MATCH), reasons };
}

// Main scoring function
export function scoreAssignment(
  employee: Employee,
  section: Section,
  context: ScoringContext
): AssignmentScore {
  const { reservations, guests, existingAssignments, sections } = context;

  const vip = calculateVipPreferenceScore(employee, section, reservations, guests);
  const allergy = calculateAllergyScore(employee, section, reservations, guests);
  const experience = calculateSectionExperienceScore(employee, section);
  const workload = calculateWorkloadScore(section, reservations, existingAssignments, sections);
  const skill = calculateSkillMatchScore(employee, section, reservations);

  const totalScore = vip.score + allergy.score + experience.score + workload.score + skill.score;
  const allReasons = [
    ...vip.reasons,
    ...allergy.reasons,
    ...experience.reasons,
    ...workload.reasons,
    ...skill.reasons,
  ].filter(Boolean);

  return {
    employeeId: employee.id,
    sectionId: section.id,
    totalScore,
    breakdown: {
      vipPreference: vip.score,
      allergyAwareness: allergy.score,
      sectionExperience: experience.score,
      workloadBalance: workload.score,
      skillMatch: skill.score,
    },
    reasons: allReasons,
  };
}

// Generate smart suggestions for all section assignments
export function generateSmartSuggestions(
  context: ScoringContext
): Map<string, AssignmentScore> {
  const { employees, sections, existingAssignments } = context;

  // Filter to servers/bartenders/hosts who can be assigned to sections
  const assignableEmployees = employees.filter((e) =>
    ['server', 'bartender', 'host'].includes(e.primaryRole)
  );

  // Filter to sections that don't have assignments yet
  const assignedSectionIds = new Set(
    existingAssignments.filter((a) => a.sectionId).map((a) => a.sectionId)
  );
  const unassignedSections = sections.filter((s) => !assignedSectionIds.has(s.id));

  // Score all employee-section combinations
  const allScores: AssignmentScore[] = [];
  for (const employee of assignableEmployees) {
    // Skip if already assigned
    if (existingAssignments.some((a) => a.employeeId === employee.id)) continue;

    for (const section of unassignedSections) {
      allScores.push(scoreAssignment(employee, section, context));
    }
  }

  // Sort by score descending
  allScores.sort((a, b) => b.totalScore - a.totalScore);

  // Greedy assignment: best score first, no duplicates
  const suggestions = new Map<string, AssignmentScore>();
  const usedEmployees = new Set<string>();
  const usedSections = new Set<string>();

  for (const score of allScores) {
    if (usedEmployees.has(score.employeeId) || usedSections.has(score.sectionId)) {
      continue;
    }
    suggestions.set(score.sectionId, score);
    usedEmployees.add(score.employeeId);
    usedSections.add(score.sectionId);
  }

  return suggestions;
}

// Get the top N suggestions for a specific section
export function getSectionSuggestions(
  sectionId: string,
  context: ScoringContext,
  limit: number = 3
): AssignmentScore[] {
  const { employees, sections, existingAssignments } = context;

  const section = sections.find((s) => s.id === sectionId);
  if (!section) return [];

  const assignableEmployees = employees.filter((e) =>
    ['server', 'bartender', 'host'].includes(e.primaryRole)
  );

  // Filter out already assigned employees
  const assignedEmployeeIds = new Set(existingAssignments.map((a) => a.employeeId));
  const availableEmployees = assignableEmployees.filter(
    (e) => !assignedEmployeeIds.has(e.id)
  );

  const scores = availableEmployees.map((employee) =>
    scoreAssignment(employee, section, context)
  );

  return scores.sort((a, b) => b.totalScore - a.totalScore).slice(0, limit);
}
